import { Button } from '@avanta/shared/components/ui/Button';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { cn } from '@avanta/shared/utils/cn';
import { ChevronRight } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AnimatedContainer } from 'components/ui/AnimatedContainer';
import { CONTAINER_MD_MAX_WIDTH } from '../../../styles';
import { AppointmentsReservePayload, type StepProps } from '../../../types';
import { BackButton } from '../../ui/BackButton';
import { StepSection } from '../../ui/StepSection';
import { Pack, PaymentSkeleton } from './components';
import { useCreatePack, useFetchSessionPacks } from './hooks';

export const PaymentStep = ({ handleBack }: StepProps): React.ReactElement => {
  const { watch } = useFormContext();
  const { mutate: createPack, isPending } = useCreatePack();
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);
  const [specialityBooked, selectedPack] = watch([
    AppointmentsReservePayload.specialityBooked,
    AppointmentsReservePayload.selectedPack,
  ]);

  const { data: sessionPacks, isLoading } = useFetchSessionPacks(specialityBooked);

  const handleNext = (): void => {
    createPack({
      grantedSessions: Number(selectedPack.grantedSessions),
      durationInDays: selectedPack.durationInDays,
      eurPerSession: selectedPack.pricePaidInEurPerSession,
    });
  };

  return (
    <AnimatedContainer>
      <div className={cn(CONTAINER_MD_MAX_WIDTH, 'flex flex-col gap-8 am-lg:gap-10')}>
        {isLoading ? (
          <PaymentSkeleton />
        ) : (
          <>
            <StepSection
              title={t('AppointmentsReserve.Steps.payment.body')}
              className="grid grid-cols-2 gap-4"
            >
              {sessionPacks?.map(pack => (
                <Pack key={pack.grantedSessions} pack={pack} />
              ))}
            </StepSection>
            <div className="flex justify-between gap-4">
              <BackButton handleBack={handleBack} />
              <Button
                type="button"
                variant="primary"
                className={cn(
                  'flex-1 am-lg:w-35 am-lg:flex-none',
                  isPending && 'flex justify-center items-center'
                )}
                size="lg"
                icon="trailing"
                onClick={handleNext}
                disabled={!selectedPack}
                isLoading={isPending}
              >
                {t('AppointmentsReserve.payment.next')}
                <ChevronRight className="size-5" />
              </Button>
            </div>
          </>
        )}
      </div>
    </AnimatedContainer>
  );
};
